/**
 * Ryan O'Dowd
 * 2024-01-20
 * © Copyright 2024 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import React, {
  useRef,
} from 'react';
import Autolink from 'react-native-autolink';
import Clipboard from '@react-native-clipboard/clipboard';
import Icon from '@react-native-vector-icons/material-design-icons';
import IconButton from '@components/common/IconButton';
import Popover from '@components/common/Popover';
import Swipeable from 'react-native-gesture-handler/ReanimatedSwipeable';
import SwipeableAction from '@components/common/SwipeableAction';
import Text from '@components/common/Text';
import {
  View,
} from 'react-native';
import {
  format as dateFnsFormat,
} from 'date-fns';
import getStyles from './styles';
import globals from '@utils/globals';
import hooks from '@hooks';
import {
  setMessageReaction,
} from '@utils/store/actions';

const iconOptions = [
  'thumb-up',
  'heart',
  'party-popper',
];

const ChatMessage = (props: ChatMessageProps) => {
  const i18n = hooks.useI18n();
  const styles = hooks.useStyles(getStyles);
  const dispatch = hooks.useTypedDispatch();

  const swipeableRef = useRef(null);

  const renderLeftActions = (progress) => {
    return (
      <SwipeableAction progress={progress}>
        <Text style={styles.timestampText}>
          {dateFnsFormat(new Date(props.message.created_at), "M/d/yy'\n'h:mmaaaaa'm'", {locale: i18n.dateFnsLocale})}
        </Text>
      </SwipeableAction>
    );
  };

  let messageBodyOverrides = styles.otherMessage;
  if (props.senderIsMe) {
    messageBodyOverrides = styles.myMessage;
  }

  return (
    <Swipeable
      ref={swipeableRef}
      renderLeftActions={renderLeftActions}
      onSwipeableWillOpen={() => swipeableRef.current?.close()}
    >
      <View style={[styles.messageWrapper, props.senderIsMe ? styles.myMessageWrapper : styles.otherMessageWrapper]}>{/* @TODO: convert to ListItem for wrinkly */}
        <View style={styles.messageWrapperInner}>
          <Popover.Wrapper
            popover={(setOpen) => {
              return (
                <Popover.Inner>
                  <View style={styles.contextMenuRow}>
                    <IconButton
                      style={styles.contextMenuButton}
                      icon='content-copy'
                      iconColor={styles.isDarkMode ? styles.colors.primaryLight : styles.colors.primary}
                      size={20}
                      onPress={() => {
                        Clipboard.setString(props.message.message_body);
                        setOpen(false);
                      }}
                    />
                    {!props.senderIsMe &&
                      <React.Fragment>
                        <View style={styles.divider} />
                        {iconOptions.map((iconName) => {
                          const isSelected = props.message.reaction_name === iconName;
                          return (
                            <IconButton
                              key={iconName}
                              style={[styles.contextMenuButton, isSelected && styles.contextMenuButtonSelected]}
                              icon={iconName}
                              iconColor={styles.isDarkMode ? styles.colors.primaryLight : styles.colors.primary}
                              size={20}
                              onPress={() => {
                                dispatch(setMessageReaction(props.message.thread_user_id, props.message.id, props.message.reaction_name === iconName ? null : iconName));
                                setOpen(false);
                              }}
                            />
                          );
                        })}
                      </React.Fragment>
                    }
                  </View>
                  {/* @TODO: color code or more info if out of date...maybe replace the NewVersion compoentn with this */}
                </Popover.Inner>
              );
            }}
          >
            <View
              style={[
                styles.touchableWrapper,
                props.senderIsMe && styles.senderIsMeTouchableWrapper,
              ]}
            >
              {!!props.message.reaction_name &&
                <View style={[styles.reactionIconContainer, props.senderIsMe && styles.senderIsMeReactionIconContainer]}>
                  <Icon style={styles.reactionIcon} name={props.message.reaction_name} />
                </View>
              }
              <Autolink
                style={[styles.messageBody, messageBodyOverrides]}
                selectable={false}
                linkStyle={styles.messageBodyLink}
                text={props.message.message_body}
                url={true}
                email={true}
              />
            </View>
          </Popover.Wrapper>
          <Text style={[styles.messageSenderName, props.senderIsMe ? styles.senderIsMeText : styles.senderIsNotMeText]}>
            {props.senderIsMe ? 'Me' : (props.message.sender_display_name || '<unkown>')}
          </Text>
          {globals.appName === 'acorn' && !props.senderIsMe && (
            <View style={styles.adminWrapper}>
              <Text style={styles.versionText}>{props.message.app_version || '<4.8.0'}, </Text>
              <Icon style={styles.platformIcon} name={props.message.platform === 'ios' ? 'apple' : props.message.platform || 'cloud-question'} />
            </View>
          )}
        </View>
      </View>
    </Swipeable>
  );
};

class ChatMessageProps {
  message: any;
  senderIsMe: boolean;
};

ChatMessage.displayName = 'ChatMessage';
export default ChatMessage;
